/**
 * Dialogue with the Targeting Strategist (§19 Tier 2) — a chat interface.
 *
 * The human pushes back; the strategist resumes its conversation (re-running
 * tools if useful), then holds or revises its call and replies directly. Your
 * message shows immediately (optimistic), a live status reflects what the
 * strategist is doing while it thinks, and the reply appends to the thread
 * (targeting.strategy.dialogue). It's a side conversation — it updates the
 * dossier but doesn't run the pipeline.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { blogRunsService } from '../../../services/blogRunsService';
import { jobsService } from '../../../services/jobsService';
import { useArticleContext } from '../ArticleContext';
import type { StrategyDialogueTurn } from '../types';

function ThinkingDots() {
  return (
    <span className="inline-flex gap-1 items-center">
      {[0, 150, 300].map((d) => (
        <span
          key={d}
          className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce"
          style={{ animationDelay: `${d}ms` }}
        />
      ))}
    </span>
  );
}

function StrategistBubble({ text, verdict, thinking }: { text: string; verdict?: string | null; thinking?: string }) {
  return (
    <div className="flex flex-col items-start max-w-[88%]">
      <div className="text-[10px] text-purple-500 font-medium mb-0.5 px-1">
        Strategist{verdict ? ` · ${verdict}` : ''}
      </div>
      <div className="text-sm text-gray-800 bg-white border border-gray-200 rounded-2xl rounded-bl-md px-3 py-2 whitespace-pre-wrap">
        {text}
      </div>
      {thinking && (
        <details className="mt-1 w-full">
          <summary className="text-[11px] text-purple-500 cursor-pointer select-none">Reasoning</summary>
          <div className="text-xs text-gray-500 whitespace-pre-wrap leading-relaxed border-l-2 border-purple-200 pl-2 mt-1">
            {thinking}
          </div>
        </details>
      )}
    </div>
  );
}

function UserBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <div className="text-sm text-white bg-indigo-600 rounded-2xl rounded-br-md px-3 py-2 max-w-[80%] whitespace-pre-wrap">
        {text}
      </div>
    </div>
  );
}

export function StrategistDialoguePanel({
  dialogue,
  verdict,
}: {
  dialogue: StrategyDialogueTurn[];
  verdict?: 'proceed' | 'reject';
}) {
  const { run, refreshRun, refreshDocument, refreshArticles } = useArticleContext();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [pending, setPending] = useState<string | null>(null);   // optimistic user message
  // Live peek into the strategist's reasoning: its real per-turn summarized
  // thinking, interleaved with the tool actions it takes.
  const [thoughts, setThoughts] = useState<Array<{ kind: 'thinking' | 'activity'; text: string }>>([]);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);

  // Auto-grow the input.
  const autosize = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, []);
  useLayoutEffect(autosize, [message, autosize]);

  // Auto-scroll the thread container itself (NOT scrollIntoView, which would
  // scroll the whole page and reset the user's position on every submit).
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [dialogue.length, pending, thoughts.length, busy]);

  const send = useCallback(async () => {
    const msg = message.trim();
    if (!msg || !run?.run_id || busy) return;
    setBusy(true);
    setError(null);
    setPending(msg);           // show it immediately
    setThoughts([]);
    setMessage('');
    try {
      const { job_id } = await blogRunsService.dialogueTargeting(run.run_id, msg);
      // Poll the turn; surface the strategist's real per-turn thinking + tool
      // actions from job progress — a live peek into its reasoning.
      for (let i = 0; i < 120; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        let job;
        try {
          job = await jobsService.get(job_id);
        } catch {
          continue; // transient
        }
        if (mounted.current) {
          const stream = (job.progress || [])
            .filter((p: any) => p?.event === 'strategist_thinking' || p?.event === 'strategist_activity')
            .map((p: any) => ({
              kind: (p.event === 'strategist_thinking' ? 'thinking' : 'activity') as 'thinking' | 'activity',
              text: (p.text || p.message || '').trim(),
            }))
            .filter((x: { text: string }) => x.text);
          setThoughts(stream);
        }
        if (job.status === 'succeeded' || job.status === 'failed' || job.status === 'cancelled') {
          await refreshRun();
          await refreshDocument();
          await refreshArticles();
          if (job.status !== 'succeeded' && mounted.current) {
            setError('The strategist could not respond — please try again.');
          }
          break;
        }
      }
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : 'Failed to send');
    } finally {
      if (mounted.current) {
        setBusy(false);
        setPending(null);      // the real turn is now in `dialogue`
        setThoughts([]);
      }
    }
  }, [message, run?.run_id, busy, refreshRun, refreshDocument, refreshArticles]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  if (!verdict) return null;

  const hasThread = dialogue.length > 0 || pending || busy;

  return (
    <div className="border border-purple-200 rounded-lg bg-white overflow-hidden">
      <div className="px-3 py-2 border-b border-purple-100 bg-purple-50">
        <div className="text-xs font-semibold text-purple-800">Discuss with the strategist</div>
        <div className="text-[11px] text-purple-600">
          Push back and it reconsiders — re-checking live data — then holds or revises its call.
        </div>
      </div>

      {hasThread && (
        <div ref={threadRef} className="px-3 py-3 space-y-3 max-h-[32rem] overflow-y-auto bg-gray-50">
          {dialogue.map((t, i) => (
            <div key={i} className="space-y-2">
              <UserBubble text={t.human} />
              <StrategistBubble text={t.strategist} verdict={t.verdict} thinking={t.thinking} />
            </div>
          ))}
          {pending && <UserBubble text={pending} />}
          {busy && (
            <div className="space-y-1.5 px-1">
              {thoughts.map((t, i) =>
                t.kind === 'thinking' ? (
                  <div key={i} className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed border-l-2 border-purple-200 pl-2">
                    {t.text}
                  </div>
                ) : (
                  <div key={i} className="text-[11px] text-purple-500 flex items-start gap-1">
                    <span className="mt-px">→</span>
                    <span>{t.text}</span>
                  </div>
                )
              )}
              <div className="flex items-center gap-2 text-xs text-purple-600">
                <ThinkingDots />
                <span className="italic">thinking…</span>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mx-3 mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>
      )}

      <div className="p-2 flex items-end gap-2">
        <textarea
          ref={inputRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={busy}
          rows={1}
          placeholder="Make your case…  (Enter to send, Shift+Enter for a new line)"
          className="flex-1 text-sm px-3 py-2 rounded-2xl border border-gray-300 bg-white resize-none focus:outline-none focus:ring-1 focus:ring-purple-400 disabled:opacity-60"
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={busy || !message.trim()}
          aria-label="Send"
          className="shrink-0 w-9 h-9 rounded-full bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40 flex items-center justify-center"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="19" x2="12" y2="5" />
            <polyline points="5 12 12 5 19 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
